import { Box, Flex } from "@chakra-ui/react";
import { useCallback } from "react";
import { MessageItem } from "./MessageItem";
import { MessagesEmptyState } from "./MessagesEmptyState";
import { useChatMessagesScroll } from "./hooks/useChatMessagesScroll";
import { useMessageTimeFormatter } from "./hooks/useMessageTimeFormatter";
import AppSpinner from "components/spinner/AppSpinner";

type Props = {
  isLoading: boolean;
  messages: any[];
  currentCustomerId?: string;
  otherPartyLabel?: string;
  onRetrySend?: (clientId: string) => void;
  scrollToBottomKey?: number;
  onAtBottomChange?: (isAtBottom: boolean) => void;
  onLoadOlder?: () => Promise<void> | void;
  isLoadingOlder?: boolean;
  hasMoreOlder?: boolean;
};

export default function MessagesList({
  isLoading,
  messages,
  currentCustomerId,
  otherPartyLabel,
  onRetrySend,
  scrollToBottomKey,
  onAtBottomChange,
  onLoadOlder,
  isLoadingOlder,
  hasMoreOlder,
}: Props) {
  const { formatMessageTime } = useMessageTimeFormatter();
  const { containerRef, handleScroll } = useChatMessagesScroll({
    scrollToBottomKey,
    onAtBottomChange,
    onLoadOlder,
    isLoadingOlder,
    hasMoreOlder,
  });

  const getId = useCallback((m: any) => {
    return String(
      m?.message_id ??
        m?.id ??
        m?.client_id ??
        `${m?.created_at ?? ""}-${m?.sender_id ?? m?.sender ?? ""}`
    );
  }, []);

  const getBody = useCallback((m: any) => {
    return String(m?.body ?? m?.message ?? "");
  }, []);

  const getFileNameFromUrl = useCallback((url: string) => {
    try {
      const clean = url.split("?")[0].split("#")[0];
      const raw = clean.split("/").pop() || "file";
      return decodeURIComponent(raw);
    } catch {
      return "file";
    }
  }, []);

  const getDisplayName = useCallback(
    (url: string, att?: any) => {
      const fileName =
        typeof att === "object" && att != null ? String(att?.fileName ?? att?.name ?? "") : "";
      return fileName || getFileNameFromUrl(url);
    },
    [getFileNameFromUrl]
  );

  const downloadFile = useCallback(async (url: string, filename: string) => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  return (
    <Box flex="1" minH={0} overflowY="auto" ref={containerRef} onScroll={handleScroll} >
      {isLoading ? (
        <Flex align="center" justify="center" minH="200px">
          <AppSpinner />
        </Flex>
      ) : (messages ?? []).length === 0 ? (
        <MessagesEmptyState />
      ) : (
        <Box>
          {(messages ?? []).map((m: any) => {
            const createdAt = m?.created_at ?? m?.createdAt;
            const displayTime = createdAt ? formatMessageTime(createdAt) : "";
            return (
              <MessageItem
                key={getId(m)}
                message={m}
                currentCustomerId={currentCustomerId}
                otherPartyLabel={otherPartyLabel}
                displayTime={displayTime}
                onRetrySend={onRetrySend}
                getId={getId}
                getBody={getBody}
                getDisplayName={getDisplayName}
                onDownload={downloadFile}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
